import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
	MfaChallengeStartResponse,
	MfaChallengeVerifyResponse,
	MfaChallengeResendResponse,
	MfaChallengeSwitchResponse,
	MfaCustomerSwitchEvaluation
} from '../models/mfa';

@Injectable()
export class MfaChallengeService {

	private completedCustomers = new Set<number>();

	constructor(private _http: HttpClient) {}

	startChallenge(method: string, challengeSessionId: string): Observable<MfaChallengeStartResponse> {
		return this._http.post<MfaChallengeStartResponse>(
			`${environment.apiUrl}/v2/MfaChallenge/Start`,
			{ method, challengeSessionId }
		);
	}

	verifyChallenge(sessionId: string, code: string, method: string, custId?: number): Observable<MfaChallengeVerifyResponse> {
		return this._http.post<MfaChallengeVerifyResponse>(
			`${environment.apiUrl}/v2/MfaChallenge/Verify`,
			{ sessionId, code, method, custId }
		);
	}

	switchMethod(sessionId: string): Observable<MfaChallengeSwitchResponse> {
		return this._http.post<MfaChallengeSwitchResponse>(
			`${environment.apiUrl}/v2/MfaChallenge/Switch`,
			{ sessionId }
		);
	}

	// Tells the SERVER the switch was abandoned. Cancelling used to be client-only, so the server still
	// believed the session was on the alternative factor and rejected a later Resend (FIX-15). Returns the
	// same shape as switchMethod — `alternativeMethod` is the factor now active again — and sends a fresh
	// OTP when that factor is email, so the caller must not also re-Start the challenge.
	cancelSwitch(sessionId: string): Observable<MfaChallengeSwitchResponse> {
		return this._http.post<MfaChallengeSwitchResponse>(
			`${environment.apiUrl}/v2/MfaChallenge/Switch/Cancel`,
			{ sessionId }
		);
	}

	resendOtp(sessionId: string): Observable<MfaChallengeResendResponse> {
		return this._http.post<MfaChallengeResendResponse>(
			`${environment.apiUrl}/v2/MfaChallenge/Resend`,
			{ sessionId }
		);
	}

	evaluateCustomerSwitch(targetCustId: number, mfaSessionToken?: string): Observable<MfaCustomerSwitchEvaluation> {
		return this._http.post<MfaCustomerSwitchEvaluation>(
			`${environment.apiUrl}/v2/MfaChallenge/CustomerSwitch/Evaluate`,
			{ targetCustId, mfaSessionToken }
		);
	}

	private mfaTokenKey(userId: string, custId?: number): string {
		// Normalize the username to lowercase so the key matches the login read path
		// (AuthenticationService.loginInetUser lowercases before reading); a mixed-case
		// canonical UserID would otherwise store under a key the read never finds.
		const normalizedUserId = (userId || '').toLowerCase();
		return custId != null
			? `Rapidship:mfaToken:${normalizedUserId}:${custId}`
			: `Rapidship:mfaToken:${normalizedUserId}`;
	}

	getMfaToken(userId: string, custId?: number): string | null {
		return localStorage.getItem(this.mfaTokenKey(userId, custId));
	}

	storeMfaToken(userId: string, token: string, custId?: number): void {
		localStorage.setItem(this.mfaTokenKey(userId, custId), token);
	}

	clearMfaToken(userId: string, custId?: number): void {
		localStorage.removeItem(this.mfaTokenKey(userId, custId));
	}

	clearAllMfaTokens(): void {
		const keysToRemove: string[] = [];
		for (let i = 0; i < localStorage.length; i++) {
			const key = localStorage.key(i);
			if (key && key.startsWith('Rapidship:mfaToken:')) {
				keysToRemove.push(key);
			}
		}
		keysToRemove.forEach(key => localStorage.removeItem(key));
	}

	markCustomerSwitchCompleted(custId: number): void {
		this.completedCustomers.add(custId);
	}

	isCustomerSwitchCompleted(custId: number): boolean {
		return this.completedCustomers.has(custId);
	}

	clearCompletedCustomers(): void {
		this.completedCustomers.clear();
	}
}
